import type { Labeled } from '@/lib/tone'

export type StudioVideoStatus = 'queued' | 'rendering' | 'done' | 'failed'

export interface StudioVideo {
  id: string
  propertyId: string
  templateId: string
  primaryColor: string
  secondaryColor: string
  musicId: string | null
  status: StudioVideoStatus
  progress: number
  error: string | null
  videoUrl: string | null
  thumbnailUrl: string | null
  durationSeconds: number | null
  sizeBytes: number | null
  renderSeconds: number | null
  createdAt: string
  finishedAt: string | null
  queuePosition?: number | null
}

export interface StudioTemplate {
  id: string
  name: string
  description: string
  minPhotos: number
  maxPhotos: number
}

export interface StudioMusicTrack {
  id: string
  name: string
  mood: string
  bpm: number | null
  source: 'platform' | 'company'
  url: string
  durationSeconds: number | null
}

export interface StudioLimits {
  maxDurationSeconds: number
  maxActive: number
  maxUploadMb: number
}

export interface StudioCatalog {
  templates: StudioTemplate[]
  music: StudioMusicTrack[]
  limits: StudioLimits
  runtime: { url: string; fontsUrl: string }
}

export interface StudioPalette {
  primary: string
  secondary: string
}

export interface StudioDraftInput {
  property: { title: string; photos: { url: string; label?: string }[] } & Record<string, unknown>
  agency: Record<string, unknown>
}

export interface StudioDraft {
  input: StudioDraftInput
  palette: StudioPalette
}

export const STUDIO_VIDEO_STATUS: Record<StudioVideoStatus, Labeled> = {
  queued: { label: 'Na fila', tone: 'neutral' },
  rendering: { label: 'Gerando', tone: 'progress' },
  done: { label: 'Pronto', tone: 'success' },
  failed: { label: 'Falhou', tone: 'danger' }
}

export const studioVideoStatus = (status: string): Labeled => STUDIO_VIDEO_STATUS[status as StudioVideoStatus] ?? { label: status, tone: 'neutral' }

export const isActiveVideo = (video: Pick<StudioVideo, 'status'>): boolean => video.status === 'queued' || video.status === 'rendering'

export const STUDIO_ACCENT = '#f2c46d'

const FIXED_PRESETS: StudioPalette[] = [
  { primary: '#5b27d7', secondary: '#f0b8ff' },
  { primary: '#1e3a5f', secondary: '#f4a261' },
  { primary: '#18161d', secondary: '#e9c46a' },
  { primary: '#a8492a', secondary: '#f6e3c4' },
  { primary: '#1f5fd6', secondary: '#9ff0cf' }
]

const HEX = /^#[0-9a-f]{6}$/i

export const isHexColor = (value: string): boolean => HEX.test(value)

export function palettePresets(agency: StudioPalette | null): StudioPalette[] {
  const presets = agency && isHexColor(agency.primary) && isHexColor(agency.secondary) ? [agency, ...FIXED_PRESETS] : FIXED_PRESETS
  const seen = new Set<string>()
  return presets.filter(preset => {
    const key = `${preset.primary.toLowerCase()}|${preset.secondary.toLowerCase()}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const samePalette = (a: StudioPalette, b: StudioPalette): boolean =>
  a.primary.toLowerCase() === b.primary.toLowerCase() && a.secondary.toLowerCase() === b.secondary.toLowerCase()

export function formatVideoDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '–'
  const total = Math.round(seconds)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '–'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / 1024 / 1024).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`
}

export const assetUrl = (apiUrl: string, path: string): string => (/^https?:\/\//.test(path) ? path : `${apiUrl.replace(/\/+$/, '')}${path}`)

export function downloadUrl(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}ik-attachment=true`
}

export function studioProgressText(video: Pick<StudioVideo, 'status' | 'progress' | 'queuePosition'>): string {
  if (video.status === 'queued') {
    const ahead = (video.queuePosition ?? 1) - 1
    return ahead > 0 ? `Na fila: ${ahead === 1 ? '1 vídeo' : `${ahead} vídeos`} antes do seu` : 'Começando…'
  }
  if (video.status === 'rendering') return `Gerando ${Math.min(99, Math.round(video.progress * 100))}%`
  if (video.status === 'done') return 'Pronto'
  return 'Não deu para gerar o vídeo'
}

export function missingPhotos(photoCount: number, template: Pick<StudioTemplate, 'minPhotos'> | undefined): number {
  return template ? Math.max(0, template.minPhotos - photoCount) : 0
}

export interface StudioPhoto {
  url: string
  label: string
}

export const MAX_PHOTO_LABEL_LENGTH = 80

export const initialPhotos = (photos: { url: string; label?: string | null }[]): StudioPhoto[] => photos.map(photo => ({ url: photo.url, label: photo.label?.trim() ?? '' }))

export const addPhoto = (selection: StudioPhoto[], photo: StudioPhoto): StudioPhoto[] => (selection.some(item => item.url === photo.url) ? selection : [...selection, photo])

export const removePhoto = (selection: StudioPhoto[], url: string): StudioPhoto[] => selection.filter(item => item.url !== url)

export const relabelPhoto = (selection: StudioPhoto[], url: string, label: string): StudioPhoto[] =>
  selection.map(item => (item.url === url ? { ...item, label: label.slice(0, MAX_PHOTO_LABEL_LENGTH) } : item))

export function movePhoto(selection: StudioPhoto[], from: number, to: number): StudioPhoto[] {
  if (from === to || from < 0 || to < 0 || from >= selection.length || to >= selection.length) return selection
  const next = [...selection]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

export const photosOutside = (photos: StudioPhoto[], selection: StudioPhoto[]): StudioPhoto[] => photos.filter(photo => !selection.some(item => item.url === photo.url))

export const photoPayload = (selection: StudioPhoto[]): { url: string; label?: string }[] =>
  selection.map(({ url, label }) => (label.trim() ? { url, label: label.trim() } : { url }))

export const ACCEPTED_MUSIC_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a', 'audio/aac', 'audio/ogg']

export function musicFileError(file: Pick<File, 'type' | 'size'>, maxUploadMb: number): string | null {
  if (!ACCEPTED_MUSIC_TYPES.includes(file.type)) return 'Formato não aceito. Use MP3, M4A, AAC ou OGG.'
  if (file.size > maxUploadMb * 1024 * 1024) return `A música passa de ${maxUploadMb} MB.`
  return null
}

export function videoFileName(title: string | null | undefined): string {
  const slug = (title ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
  return `${slug || 'video-imovel'}.mp4`
}

export const STUDIO_ERROR_MESSAGES: Record<string, string> = {
  STUDIO_TOO_MANY_ACTIVE: 'Espere os vídeos em andamento terminarem para pedir outro.',
  STUDIO_NOT_ENOUGH_PHOTOS: 'Este modelo precisa de mais fotos. Marque mais fotos ou adicione no cadastro do imóvel.',
  STUDIO_PHOTO_INVALID: 'Uma das fotos escolhidas saiu do cadastro do imóvel. Recarregue a página e tente de novo.',
  STUDIO_TEMPLATE_NOT_FOUND: 'Este modelo não está mais disponível.',
  STUDIO_MUSIC_NOT_FOUND: 'Esta música não está mais disponível. Escolha outra.',
  STUDIO_MUSIC_INVALID: 'Não deu para usar esta música. Envie um MP3, M4A, AAC ou OGG de até 20 MB com pelo menos 5 segundos.'
}
